import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { Link } from "react-router-dom";
import { TableData } from "../../../core/data/interface";
import Table from "../../../core/common/dataTable/index";
import CampaignModal from "../../../core/modals/campaign_modal";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import {
  getCampaignHeader,
  getCampaigns,
  getMailTemplates,
  sendBulk,
} from "./campaignAPI";
import { CAMPAIGN_STATUSES, CAMPAIGN_TYPES } from "./config";
import { Pagination } from "antd";
import CampaignMemberModal from "../../../core/modals/campaign_member_modal";
import Select from "react-select";

interface MailTemplate {
  id: number;
  uuid: string;
  name: string;
}
export type CampaignType = {
  id: number;
  uuid: string;
  name: string;
  type: string;
  progress: string;
  opened: number | null;
  clicked: number | null;
  delivered: number | null;
  conversation: number | null;
  startDate: string;
  endDate: string;
  createdDate: string;
  status: string | null;
  numOfLeads: number;
  created: string;
  Action: string;
  key: string;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatPercentage = (value: number | null | undefined): string => {
  return value !== null && value !== undefined ? `${value.toFixed(1)}%` : "0%";
};

type CampaignHeader = {
  campaigns: number;
  sent: number;
  opened: number;
  completed: number;
};

const defaultCampaignHeaderData = {
  campaigns: 0,
  sent: 0,
  opened: 0,
  completed: 0,
};

type CampaignFilter = {
  pageNo: number;
  pageSize: number;
  name?: string;
  type?: string;
  startDate?: Date;
  endDate?: Date;
  status?: string;
};

const defaultCampaignFilter = {
  pageNo: 0,
  pageSize: 5,
  name: "",
  type: undefined,
  startDate: undefined,
  endDate: undefined,
  status: undefined,
};

const Campaign = () => {
  const [filter, setFilter] = useState<CampaignFilter>(defaultCampaignFilter);

  const [totalElements, setTotalElements] = useState<number>(0);

  const [data, setData] = useState<CampaignType[]>([]);

  const [recordId, setRecordId] = useState<number>();

  const [action, setAction] = useState<"edit" | "delete">();

  const [reload, setReload] = useState<boolean>(false);

  const [campaignHeader, setCampaignHeader] = useState<CampaignHeader>(
    defaultCampaignHeaderData
  );

  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      if (filter.name !== searchValue) {
        // Only update if the value actually changed
        setFilter((prevFilter) => ({ ...prevFilter, name: searchValue }));
      }
    }, 500); // Delay of 500ms

    return () => clearTimeout(handler); // Cleanup on every keystroke
  }, [searchValue, filter.name]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const { data } = await getCampaigns(filter);

        const cData = data?.data?.content;

        const campaignData = cData.map((d: CampaignType, index: number) => ({
          ...d,
          progress: `${formatPercentage(
            d.opened
          )} Opened\n \n  ${formatPercentage(
            d.clicked
          )} Clicked\n \n  ${formatPercentage(
            d.delivered
          )} Delivered\n \n  ${formatPercentage(d.conversation)} Conversation`,
          numOfLeads: d.numOfLeads,
          created: formatDate(d.createdDate),
          Action: "Edit      Delete",
          key: `${index}`,
          startDate: formatDate(d.startDate),
          endDate: formatDate(d.endDate),
        }));
        console.log(campaignData);
        setData(campaignData);
        setTotalElements(data?.data?.totalElements);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
      }
    };

    fetchCampaignData();
  }, [reload, filter]);

  useEffect(() => {
    const fetchCampaignHeader = async () => {
      const { data } = await getCampaignHeader();
      setCampaignHeader(data?.data);
    };

    fetchCampaignHeader();
  }, []);

  const handleEditCampaign = (id: number) => {
    setRecordId(id);
    setAction("edit");
  };

  const handleDeleteCampaign = (id: number) => {
    setRecordId(id);
    setAction("delete");
  };

  const [stars, setStars] = useState<{ [key: number]: boolean }>({});
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [mailTemplateOptions, setMailTemplateOptions] = useState<
    MailTemplate[]
  >([]);
  const fetchMailTemplate = async () => {
    try {
      const response = await getMailTemplates();
      setMailTemplateOptions(response.data.data || []);
    } catch (err) {
      console.error("Error fetching mail templates:", err);
    }
  };

  useEffect(() => {
    fetchMailTemplate();
  }, []);

  const [mailData, setMailData] = useState({
    templateId: 0,
    fromEmail: "sep490@cotrangvietvutamthu.com",
    recipients: [] as string[],
    type: null,
    campaignId: 0,
  });

  const handleSendEmail = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        alert("User ID not found!");
        return;
      }
      if (recordId && selectedTemplate) {
        setMailData((prev) => ({
          ...prev,
          templateId: selectedTemplate,
          campaignId: recordId,
        }));
      }

      await sendBulk({
        ...mailData,
        templateId: selectedTemplate ?? 0,
        campaignId: recordId ?? 0,
      });
      alert("Email sent successfully!");
    } catch (error) {
      alert("Error sending email!");
    }
  };

  React.useEffect(() => {
    const initializeStarsState = () => {
      const starsState: { [key: number]: boolean } = {};
      data.forEach((item, index) => {
        starsState[index] = false;
      });
      setStars(starsState);
    };

    initializeStarsState();
  }, [data]);

  const handleStarToggle = (index: number) => {
    setStars((prevStars) => ({
      ...prevStars,
      [index]: !prevStars[index],
    }));
  };

  const columns = [
    {
      title: "",
      dataIndex: "",
      render: (text: string, record: any, index: number) => (
        <div
          className={`set-star rating-select ${stars[index] ? "filled" : ""}`}
          onClick={() => handleStarToggle(index)}
        >
          <i className="fa fa-star"></i>
        </div>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: TableData, b: TableData) => a.name.localeCompare(b.name),
    },
    {
      title: "Type",
      dataIndex: "type",
      sorter: (a: TableData, b: TableData) => a.type.localeCompare(b.type),
      render: (text: string) => {
        const typeObj = CAMPAIGN_TYPES.find(
          (cpType) => text && cpType.value.toLowerCase() === text.toLowerCase()
        );
        return <div>{typeObj ? typeObj.label : "Unknown"}</div>;
      },
    },
    {
      title: "Progress",
      dataIndex: "progress",
      // width: 500, // Specify the width here
      render: (text: string) => {
        const progressData = text
          .split("\n")
          .filter((item) => item.trim() !== ""); // Split progress data by newline and filter out empty strings
        return (
          <ul className="list-progress">
            {progressData.map((item, index) => {
              const [percentage, label] = item
                .split("%")
                .map((str) => str.trim()); // Split each item into percentage and label
              return (
                <li key={index}>
                  <h6>{percentage}</h6>
                  <p>{label}</p>
                </li>
              );
            })}
          </ul>
        );
      },
      sorter: (a: TableData, b: TableData) =>
        a.progress.length - b.progress.length,
    },
    {
      title: "Members",
      dataIndex: "numOfLeads",
      render: (text: string, record: any) => (
        <Link
          to="#"
          style={{
            cursor: "pointer",
            fontSize: "16px",
            display: "flex",
            justifyContent: "center",
            color: "#0d6efd",
            textDecoration: "underline",
          }}
          data-bs-toggle="modal"
          data-bs-target="#add_campaign_member"
          onClick={() => {
            setRecordId(record.id);
          }}
        >
          {record.numOfLeads}
        </Link>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      sorter: (a: TableData, b: TableData) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      sorter: (a: TableData, b: TableData) =>
        new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: (a: TableData, b: TableData) => a.status.localeCompare(b.status),
      render: (text: string) => (
        <div>
          {text === "complete" && (
            <span className="badge badge-pill badge-status bg-success">
              {text}
            </span>
          )}
          {text === "running" && (
            <span className="badge badge-pill badge-status bg-warning">
              {text}
            </span>
          )}

          {text === "open" && (
            <span className="badge badge-pill badge-status bg-info">
              {text}
            </span>
          )}
          {/* {text === "running" && (
            <span className="badge badge-pill badge-status bg-green">
              {text}
            </span>
          )} */}
        </div>
      ),
    },

    {
      title: "Created",
      dataIndex: "created",
      sorter: (a: TableData, b: TableData) =>
        new Date(a.created).getTime() - new Date(b.created).getTime(),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any, index: number) => (
        <div className="dropdown table-action">
          <Link
            to="#"
            className="action-icon"
            data-bs-toggle="dropdown"
            aria-expanded="true"
          >
            <i className="fa fa-ellipsis-v"></i>
          </Link>
          <div
            className="dropdown-menu dropdown-menu-right"
            style={{
              position: "absolute",
              inset: "0px auto auto 0px",
              margin: "0px",
              transform: "translate3d(-99.3333px, 35.3333px, 0px)",
            }}
            data-popper-placement="bottom-start"
          >
            <Link
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#chooseMailTemplate"
              onClick={() => setRecordId(record.id)}
              to="#"
            >
              <i className="ti ti-mail" />
              Send Mail
            </Link>
            <Link
              onClick={() => handleEditCampaign(record?.id)}
              className="dropdown-item"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit"
              to="#"
            >
              <i className="ti ti-edit text-blue"></i> Edit
            </Link>
            <Link
              onClick={() => handleDeleteCampaign(record?.id)}
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#delete_campaign"
            >
              <i className="ti ti-trash text-danger"></i> Delete
            </Link>
          </div>
        </div>
      ),
    },
  ];
  const initialSettings = {
    endDate: new Date(),
    ranges: {
      "Last 30 Days": [
        dayjs().subtract(30, "days").startOf("day").toDate(),
        new Date(),
      ],
      "Last 7 Days": [
        dayjs().subtract(7, "days").startOf("day").toDate(),
        new Date(),
      ],
      "Last Month": [
        dayjs().subtract(1, "month").startOf("month").startOf("day").toDate(),
        dayjs().subtract(1, "month").endOf("month").endOf("day").toDate(),
      ],
      "This Month": [
        dayjs().startOf("month").startOf("day").toDate(),
        dayjs().endOf("month").endOf("day").toDate(),
      ],
      Today: [dayjs().startOf("day").toDate(), dayjs().endOf("day").toDate()],
      Yesterday: [
        dayjs().subtract(1, "day").startOf("day").toDate(),
        dayjs().subtract(1, "day").endOf("day").toDate(),
      ],
    },
    startDate: dayjs().subtract(7, "days").startOf("day").toDate(), // Set "Last 7 Days" as default
    timePicker: false,
  };

  const handleDateRangePickerCallback = (start: Date, end: Date) => {
    setFilter({
      ...filter,
      startDate: dayjs(start).toDate(),
      endDate: dayjs(end).toDate(),
    });
  };

  const handleFilterType = (type: string) => {
    if (filter.type === type) {
      setFilter({ ...filter, type: undefined });
    } else {
      setFilter({ ...filter, type });
    }
  };

  const handleFilterStatus = (status: string) => {
    if (filter.status === status) {
      setFilter({ ...filter, status: undefined });
    } else {
      setFilter({ ...filter, status });
    }
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">Campaign</h4>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              {/* Campaign Status */}
              <div className="row">
                <div className="col-xl-3 col-lg-6">
                  <div className="campaign-box bg-danger-light">
                    <div className="campaign-img">
                      <span>
                        <i className="ti ti-brand-campaignmonitor" />
                      </span>
                      <p>Campaign</p>
                    </div>
                    <h2>{campaignHeader.campaigns}</h2>
                  </div>
                </div>
                <div className="col-xl-3 col-lg-6">
                  <div className="campaign-box bg-warning-light">
                    <div className="campaign-img">
                      <span>
                        <i className="ti ti-send" />
                      </span>
                      <p>Sent</p>
                    </div>
                    <h2>{campaignHeader.sent}</h2>
                  </div>
                </div>
                <div className="col-xl-3 col-lg-6">
                  <div className="campaign-box bg-purple-light">
                    <div className="campaign-img">
                      <span>
                        <i className="ti ti-brand-feedly" />
                      </span>
                      <p>Opened</p>
                    </div>
                    <h2>{campaignHeader.opened}</h2>
                  </div>
                </div>
                <div className="col-xl-3 col-lg-6">
                  <div className="campaign-box bg-success-light">
                    <div className="campaign-img">
                      <span>
                        <i className="ti ti-brand-pocket" />
                      </span>
                      <p>Completed</p>
                    </div>
                    <h2>{campaignHeader.completed}</h2>
                  </div>
                </div>
              </div>
              {/* /Campaign Status */}
              {/* Campaign List */}
              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="icon-form mb-3 mb-sm-0">
                        <span className="form-icon">
                          <i className="ti ti-search" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search Campaign"
                          name="search"
                          value={searchValue}
                          onChange={handleSearch}
                        />
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add"
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add New Campaign
                        </Link>
                      </div>
                    </div>
                  </div>
                  {/* /Search */}
                </div>

                <div className="card-body">
                  <>
                    {/* Filter */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                      <div className="d-flex align-items-center flex-wrap row-gap-2">
                        <div className="icon-form">
                          <span className="form-icon">
                            <i className="ti ti-calendar" />
                          </span>
                          <DateRangePicker
                            initialSettings={initialSettings}
                            onCallback={handleDateRangePickerCallback}
                          >
                            <input
                              className="form-control bookingrange"
                              type="text"
                            />
                          </DateRangePicker>
                        </div>
                      </div>
                    </div>
                    {/* /Filter */}
                  </>
                  {/* Choose Mail Template */}
                  <div
                    className="modal custom-modal fade"
                    id="chooseMailTemplate"
                    tabIndex={-1}
                    role="dialog"
                  >
                    <div className="modal-dialog modal-dialog-centered">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">Choose Mail Template</h5>
                          <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                          ></button>
                        </div>

                        <div className="modal-body">
                          <Select
                            getOptionLabel={(mail) => mail.name}
                            getOptionValue={(mail) => mail.id.toString()}
                            options={mailTemplateOptions}
                            onChange={(selectedOption) =>
                              setSelectedTemplate(
                                selectedOption ? selectedOption.id : null
                              )
                            }
                            value={
                              mailTemplateOptions.find(
                                (m) => m.id === selectedTemplate
                              ) || null
                            }
                            placeholder="Select template"
                          />
                        </div>

                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-light"
                            data-bs-dismiss="modal"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSendEmail}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Campaign List */}
                  <div className="col-sm-12 table-responsive">
                    <Table dataSource={data} columns={columns} />
                  </div>

                  <div className="row align-items-center mt-4">
                    <div className="col-md-12">
                      <Pagination
                        current={filter.pageNo + 1} // Current page number
                        total={totalElements} // Total number of items
                        pageSize={filter.pageSize} // Items per page
                        onChange={(page) =>
                          setFilter({ ...filter, pageNo: page - 1 })
                        } // Callback function
                      />
                    </div>
                  </div>
                  {/* /Campaign List */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <CampaignModal
        action={action}
        recordId={recordId}
        setRecordId={setRecordId}
        reload={reload}
        setReload={setReload}
      />
      <CampaignMemberModal recordId={recordId} />
    </>
  );
};

export default Campaign;
